import React, { memo, useEffect, useMemo, useState } from 'react'
import HalspanContainer from '@components/Container';
import PageBanner from '@components/PageBanner';
import RibbonTemplate from '@components/RibbonTemplate';
import Facade from '@whitecobalt/tungsten/esm/components/Facade';
import Icon from '@whitecobalt/tungsten/esm/components/Icon';
import FileKeyLoader from '@whitecobalt/tungsten/esm/components/FileKeyLoader';
import { useScreenTitle } from '@whitecobalt/tungsten/hooks/useScreenTitle'
import { useEffectMounted } from '@whitecobalt/tungsten/hooks/useEffectMounted'
import { ProductsQuery, ProductsType } from '@services/query/ProductSelectorQueries';
import { useLazyQuery } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import ProductListContainer from '../Prefabs/ProductListContainer';
import ProductThumbnail from '../Prefabs/ProductThumbnail';
import ProductFilter, { StateProps } from '../Prefabs/ProductFilter';
import { ShopProductHardwareQuery, ShopProductHardwareQueryType } from '@services/query/EcommerceQueries';
import { emptyArray } from '@whitecobalt/tungsten/esm/common/utils/assets';

interface OtherAccessoryListProps {
	type: 'signage' | 'smart-tags' | 'closer-protection' | 'hinge-protection' | 'lock-protection' | 'air-transfer-grill' | 'accessories';
	menuGroup?: string;
	menuDescription?: string;
	shopLinks: {
        label: string;
        value: string;
    }[]
	productTypeURL?: string;
	productCategoryURL?: string;
}

const OtherAccessoryList: React.FunctionComponent<OtherAccessoryListProps> = memo(({type, menuGroup, menuDescription, shopLinks,  productTypeURL, productCategoryURL}) => {
    const isSignage = type === 'signage'
    const isSmartTags = type === 'smart-tags'
    const isCloserProtection = type === 'closer-protection'
    const isHingeProtection = type === 'hinge-protection'
    const isLockProtection = type === 'lock-protection'
    const isAirTransferGrill = type === 'air-transfer-grill'
    const isAccessory = type === 'accessories'


    const title = isSignage 
        ? 'Signage' 
        : isSmartTags 
            ? 'Smart Tags' 
            : isCloserProtection 
                ? 'Closer Protections' 
                : isHingeProtection 
                    ? 'Hinge Protections' 
                    : isLockProtection 
                        ? 'Lock Protections' 
                        : isAirTransferGrill 
                            ? 'Air Transfer Grill' 
                            : 'Accessories'

    useScreenTitle(`Halspan Store - ${title}`);
	
	const history = useHistory()

	const [filters, setFilters] = useState<Pick<StateProps, 'price' | 'includePoA'>>({
		price: [1, 1000],
		includePoA: null
	})

	const [loadProducts, request] = useLazyQuery<ShopProductHardwareQueryType>(ShopProductHardwareQuery, {
		fetchPolicy: 'no-cache'
	})
	
	const variables = {
		request: {
            ProductTypeIDs: isSignage 
                ? [15] 
                : isSmartTags 
                    ? [16] 
                    : isCloserProtection 
                        ? [6]
                        : isHingeProtection 
                            ? [7]
                            : isLockProtection 
                                ? [10]
                                : isAirTransferGrill 
                                    ? [11]
                                    : [17]
			,
			IncludePOA: filters.includePoA,
			MinPrice: filters.price[0],
			MaxPrice: filters.price[1]
		}
	}

	useEffect(() => {
		loadProducts({
			variables
		})
	}, [type, filters])

    const data = request.data?.ShopProductHardware.items || emptyArray

	const fileKeys = useMemo(() => {
		return data.filter((each) => !!each.Product.ProductImages[0]?.ImageUrl).map((each) => {
			return each.Product.ProductImages[0]?.ImageUrl
		})
	}, [data])

	const multiFetchPayload = useMemo(() => ({
		"maxWidthInPX": 175,
		"maxHeightInPX": 175,
	}), [])

    return (
		<>
			<div className="row">
				<div className="col-md-3 wc-sidebar-menu wc-products-home-side-menu wc-search-products">
                    <ProductFilter 
						shopLinks={shopLinks}
                        productTypeName={ menuGroup ? menuGroup : isSmartTags || isSignage ? title : "Other Products"}
						productCategory={isSmartTags || isSignage ? undefined : title}
						productTypeURL={productTypeURL}
						productCategoryURL={productCategoryURL}
						menuDescription={menuDescription}
						hasPrice={!isSmartTags}
						onChange={(events) => {
							setFilters(prev => ({
								...prev,
								...events
							}))
						}} />
				</div>
				<div className="col-md-9">
					<div className="row">
						<FileKeyLoader.MultiFetch
							fileKeys={fileKeys}
							payload={multiFetchPayload}
							cache
							cacheUniqueString="175px"
						>
							{data.length > 0 ? data.map((item) => {
								return (
									<div key={item.Product.ID} className="col-sm-6 col-lg-4 col-xl-3">
										<ProductThumbnail 
											productName={item.Product.DisplayName} 
											fileKey={item.Product.ProductImages[0]?.ImageUrl}
											url={`/store/product/view/${item.Product.ID}`}/>
									</div>
								)
							}) : request.loading ? (
								new Array(8).fill(0).map((_: any, index) => (
									<div key={index} className="col-sm-6 col-lg-4 col-xl-3 wc-product-search-item">
										<div className="product-thumbnail">
											<h4><Facade title rows={0} padding={false} rowListStyle={{margin: 0}} /></h4>
											<Facade rows={1} padding={false} rowListStyle={{margin: 0}} rowStyle={{ borderRadius: 0, width: '100%', height: 150}} />
										</div>
									</div>
								))
							) : (
								<div className="d-flex justify-content-center w-100">
									<div className="text-center my-5">
										<Icon name="inbox" size={25}/>
										<h4 className="mt-2">Halspan accessory products range will be launched soon – watch this space.</h4>
									</div>
								</div>
							)}
						</FileKeyLoader.MultiFetch>
					</div>
					{/* <div className="container pb-4">
						<div className="row">
							<div className="col-md-12 wc-you-need-this-banner-area wc-non-halspan-info wc-search-product-help-area">
							<p>Need help finding the right product?</p>
							<input type="submit" className="form-control wc-door-system-name-btn" title="Door system name here >" />
							</div>
						</div>
					</div> */}
				</div>
			</div>   
		</>
    )
})

export default OtherAccessoryList